// Health → Vet visits: what the vet said, did and prescribed — written down
// at the time, so nothing is lost between the two of you, the next visit
// and the insurance claim. Recording a visit also ticks off the care plan,
// logs his weight and medicines, and books any follow-up.

import { FileText, Pill, Phone, Plus, Stethoscope, Trash2 } from 'lucide-react';
import { useMemo, useState, useSyncExternalStore } from 'react';
import { currentPerson } from '../../data/actions.ts';
import { devicePrefs } from '../../data/device.ts';
import { useCollection, useDevice, useProfile } from '../../data/hooks.ts';
import { uid } from '../../data/ids.ts';
import { deleteVetVisit, saveVetVisit } from '../../data/vetVisits.ts';
import { careState, openCare } from '../../domain/care.ts';
import { formatTime, toISODate } from '../../domain/time.ts';
import type { VetVisit } from '../../domain/types.ts';
import { navigate } from '../../app/router.ts';
import { Button, Card, CardHeader, Empty, Field, Segmented, Sheet } from '../../ui/primitives.tsx';
import { toast } from '../../ui/toast.tsx';
import { AppointmentsCard } from './Appointments.tsx';

// --- Open the sheet from anywhere (e.g. Health watch → "Spoken to the vet") ---------------

let editing: { visit: VetVisit; isNew: boolean } | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function openVetVisit(draft: Partial<VetVisit> = {}, existing?: VetVisit) {
  // Sitters can read visits but not record them (the server wouldn't keep them).
  if (devicePrefs().guest) {
    if (!existing) toast({ message: 'Only the owners can record vet visits', detail: 'Tell them what the vet said — or add it as a note.', tone: 'bad' });
    return;
  }
  editing = existing ? { visit: existing, isNew: false } : { visit: { id: uid(), at: Date.now(), kind: 'visit', by: currentPerson(), ...draft }, isNew: true };
  emit();
}

function closeVisit() {
  editing = null;
  emit();
}

export function VetVisitHost() {
  const s = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => editing,
  );
  if (!s) return null;
  return <VisitSheet key={s.visit.id} initial={s.visit} isNew={s.isNew} />;
}

// --- The list ------------------------------------------------------------------------

export function VetVisits() {
  const guest = !!useDevice().guest;
  const visits = useCollection('vetVisits');
  const care = useCollection('care');
  const sorted = useMemo(() => [...visits].sort((a, b) => b.at - a.at), [visits]);
  const cost = visits.reduce((t, v) => t + (v.cost ?? 0), 0);
  return (
    <div className="stack">
      <AppointmentsCard />
      <Card>
        <CardHeader
          title="Vet visits & calls"
          icon={Stethoscope}
          sub="Write it down while it’s fresh — what they found, what they gave him, what to watch for."
          action={
            <div className="row">
              <Button size="sm" icon={FileText} onClick={() => navigate('/vet-report')}>
                Report for the vet
              </Button>
              {!guest && (
                <Button size="sm" variant="primary" icon={Plus} onClick={() => openVetVisit()}>
                  Record a visit
                </Button>
              )}
            </div>
          }
        />
        {cost > 0 && <p className="muted small">£{cost.toFixed(2)} in vet costs recorded so far.</p>}
      </Card>
      {sorted.length ? (
        sorted.map((v) => {
          const done = (v.careDone ?? []).map((id) => care.find((c) => c.id === id)?.title).filter(Boolean);
          return (
            <Card key={v.id} className="visit-card" onClick={guest ? undefined : () => openVetVisit({}, v)}>
              <div className="visit-head">
                <span className="visit-icon">{v.kind === 'call' ? <Phone aria-hidden /> : <Stethoscope aria-hidden />}</span>
                <div>
                  <strong>{v.reason || (v.kind === 'call' ? 'Phone call' : 'Vet visit')}</strong>
                  <span className="muted small">
                    {new Date(v.at).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} · {formatTime(v.at)} · {v.practice ?? 'Vet'}
                    {v.vet ? ` · ${v.vet}` : ''}
                  </span>
                </div>
              </div>
              {v.notes?.trim() && <p className="visit-notes">{v.notes}</p>}
              <div className="visit-chips">
                {v.weightKg ? <span className="chip">{v.weightKg} kg</span> : null}
                {done.map((t) => (
                  <span key={t} className="chip on">
                    ✓ {t}
                  </span>
                ))}
                {(v.meds ?? []).map((m, i) => (
                  <span key={i} className="chip">
                    <Pill aria-hidden className="chip-icon" /> {m.name}
                    {m.dose ? ` · ${m.dose}` : ''}
                  </span>
                ))}
                {v.followUp?.date && <span className="chip">Follow-up {new Date(v.followUp.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>}
                {v.cost ? <span className="chip">£{v.cost.toFixed(2)}{v.claim ? ' · to claim' : ''}</span> : null}
              </div>
            </Card>
          );
        })
      ) : (
        <Card>
          <Empty icon={Stethoscope} title="No visits yet">
            His first check-up is usually within a few days of coming home. Tap <strong>Record a visit</strong> at the vet — or straight after, while it’s fresh.
          </Empty>
        </Card>
      )}
    </div>
  );
}

// --- The sheet -----------------------------------------------------------------------

function VisitSheet({ initial, isNew }: { initial: VetVisit; isNew: boolean }) {
  const profile = useProfile();
  const contacts = useCollection('contacts');
  const care = useCollection('care');
  const [v, setV] = useState<VetVisit>(initial);
  const set = (patch: Partial<VetVisit>) => setV((x) => ({ ...x, ...patch }));
  const vets = contacts.filter((c) => c.role === 'vet' || c.role === 'emergency-vet');
  const [answers, setAnswers] = useState<Record<string, string>>(() => Object.fromEntries((initial.answers ?? []).map((a) => [a.q, a.a])));
  const questions = [...new Set([...(initial.answers ?? []).map((a) => a.q), ...(profile.vetQuestions ?? [])])];

  // Care jobs that could have been done at this visit: open ones due within about six weeks either side, plus any already ticked.
  const day = toISODate(v.at);
  const candidates = useMemo(() => {
    const near = (d?: string) => !d || Math.abs(new Date(d).getTime() - new Date(day).getTime()) < 45 * 86_400_000;
    const open = openCare(care).filter((c) => ['vaccination', 'worming', 'flea', 'vet', 'weigh', 'admin'].includes(c.kind) && near(c.due) && careState(c) !== 'unscheduled');
    const ticked = care.filter((c) => (initial.careDone ?? []).includes(c.id));
    return [...new Map([...ticked, ...open].map((c) => [c.id, c])).values()];
  }, [care, day, initial.careDone]);
  const toggleCare = (id: string) => set({ careDone: (v.careDone ?? []).includes(id) ? (v.careDone ?? []).filter((x) => x !== id) : [...(v.careDone ?? []), id] });

  const meds = v.meds ?? [];
  const setMed = (i: number, patch: Partial<NonNullable<VetVisit['meds']>[number]>) => set({ meds: meds.map((m, j) => (j === i ? { ...m, ...patch } : m)) });

  const local = new Date(v.at - new Date(v.at).getTimezoneOffset() * 60_000).toISOString();
  const save = () => {
    const answered = Object.entries(answers)
      .filter(([, a]) => a.trim())
      .map(([q, a]) => ({ q, a: a.trim() }));
    const contact = vets.find((c) => c.id === v.contactId);
    const visit: VetVisit = {
      ...v,
      // A weight typed in, then hidden by switching to "Phone call", isn't a weigh-in.
      weightKg: v.kind === 'visit' ? v.weightKg : undefined,
      practice: contact?.name ?? (v.practice?.trim() || undefined),
      meds: meds.filter((m) => m.name.trim()),
      answers: answered.length ? answered : undefined,
      followUp: v.followUp?.date ? v.followUp : undefined,
    };
    const ticked = saveVetVisit(visit, isNew ? undefined : initial, answered.map((a) => a.q));
    const bits = [
      ticked ? `${ticked} care job${ticked > 1 ? 's' : ''} ticked off` : '',
      visit.weightKg ? 'weight logged' : '',
      visit.followUp ? 'follow-up added to the care plan' : '',
    ].filter(Boolean);
    toast({ message: isNew ? 'Visit saved' : 'Visit updated', detail: bits.join(' · ') || undefined, tone: 'good' });
    closeVisit();
  };

  return (
    <Sheet
      open
      wide
      onClose={closeVisit}
      title={isNew ? (v.kind === 'call' ? 'Record a call with the vet' : 'Record a vet visit') : 'Vet visit'}
      footer={
        <>
          {!isNew && (
            <Button
              variant="ghost"
              icon={Trash2}
              onClick={() => {
                deleteVetVisit(initial);
                toast({ message: 'Visit deleted', tone: 'good' });
                closeVisit();
              }}
            >
              Delete
            </Button>
          )}
          <Button variant="primary" onClick={save}>
            Save
          </Button>
        </>
      }
    >
      <div className="form visit-form">
        <div className="form-grid">
          <Field label="Type">
            <Segmented
              value={v.kind}
              onChange={(kind) => set({ kind })}
              options={[
                { value: 'visit', label: 'At the vet' },
                { value: 'call', label: 'Phone call' },
              ]}
            />
          </Field>
          <Field label="When">
            <input className="input" type="datetime-local" value={local.slice(0, 16)} onChange={(e) => e.target.value && set({ at: new Date(e.target.value).getTime() })} />
          </Field>
        </div>
        <div className="form-grid">
          <Field label="Practice">
            <div className="visit-practice">
              {vets.length > 0 && (
                <select className="input" aria-label="Practice" value={v.contactId && vets.some((c) => c.id === v.contactId) ? v.contactId : ''} onChange={(e) => set({ contactId: e.target.value || undefined, practice: vets.find((c) => c.id === e.target.value)?.name ?? '' })}>
                  <option value="">{vets.length ? 'Other…' : 'Practice'}</option>
                  {vets.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
              {!(v.contactId && vets.some((c) => c.id === v.contactId)) && (
                <input className="input" aria-label="Practice name" value={v.practice ?? ''} placeholder="e.g. Queens Crescent Vets" onChange={(e) => set({ practice: e.target.value })} />
              )}
            </div>
          </Field>
          <Field label="Who you saw">
            <input className="input" value={v.vet ?? ''} placeholder="e.g. Dr Patel" onChange={(e) => set({ vet: e.target.value })} />
          </Field>
        </div>
        <Field label="Why">
          <input className="input" value={v.reason ?? ''} placeholder="e.g. First health check and 2nd vaccination" onChange={(e) => set({ reason: e.target.value })} />
        </Field>

        {candidates.length > 0 && (
          <Field label="Done at this visit" hint="Ticks them off the care plan — repeating ones book the next date.">
            <div className="chips">
              {candidates.map((c) => (
                <button key={c.id} type="button" className={`chip${(v.careDone ?? []).includes(c.id) ? ' on' : ''}`} onClick={() => toggleCare(c.id)}>
                  {(v.careDone ?? []).includes(c.id) ? '✓ ' : ''}
                  {c.title}
                </button>
              ))}
            </div>
          </Field>
        )}

        {v.kind === 'visit' && (
          <Field label="Weight at the vet (kg)" hint="Added to his weight chart.">
            <input className="input" type="number" inputMode="decimal" step="0.01" min="0" value={v.weightKg ?? ''} onChange={(e) => set({ weightKg: e.target.value ? Number(e.target.value) : undefined })} />
          </Field>
        )}

        {questions.length > 0 && (
          <Field label="Your questions" hint="Answered ones come off the list (they’re kept here with the visit).">
            <div className="visit-questions">
              {questions.map((q) => (
                <label key={q} className="visit-q">
                  <span>{q}</span>
                  <input className="input" value={answers[q] ?? ''} placeholder="What they said…" onChange={(e) => setAnswers((a) => ({ ...a, [q]: e.target.value }))} />
                </label>
              ))}
            </div>
          </Field>
        )}

        <Field label="What the vet said">
          <textarea className="input" rows={5} value={v.notes ?? ''} placeholder="Findings, advice, what to watch for, when to call back…" onChange={(e) => set({ notes: e.target.value })} />
        </Field>

        <Field label="Given or prescribed" hint="Each is logged; a course gets a “last dose” reminder.">
          <div className="visit-meds">
            {meds.map((m, i) => (
              <div key={i} className="visit-med">
                <input className="input" value={m.name} placeholder="Medicine / treatment" onChange={(e) => setMed(i, { name: e.target.value })} />
                <input className="input" value={m.dose ?? ''} placeholder="Dose" onChange={(e) => setMed(i, { dose: e.target.value })} />
                <label className="visit-days">
                  <input className="input" type="number" min="1" value={m.days ?? ''} placeholder="–" aria-label="For how many days" onChange={(e) => setMed(i, { days: e.target.value ? Number(e.target.value) : undefined })} />
                  <span className="muted small">days</span>
                </label>
                <input className="input visit-med-how" value={m.instructions ?? ''} placeholder="How — e.g. twice a day with food" onChange={(e) => setMed(i, { instructions: e.target.value })} />
              </div>
            ))}
            <Button size="sm" icon={Plus} onClick={() => set({ meds: [...meds, { id: uid(), name: '' }] })}>
              Add
            </Button>
          </div>
        </Field>

        <div className="form-grid">
          <Field label="Follow-up" hint="Goes on the care plan and your calendar.">
            <input className="input" type="date" value={v.followUp?.date ?? ''} onChange={(e) => set({ followUp: e.target.value ? { what: v.followUp?.what ?? '', date: e.target.value } : undefined })} />
          </Field>
          <Field label="For">
            <input className="input" value={v.followUp?.what ?? ''} placeholder="e.g. Recheck ears" disabled={!v.followUp?.date} onChange={(e) => set({ followUp: { date: v.followUp!.date, what: e.target.value } })} />
          </Field>
        </div>
        <div className="form-grid">
          <Field label="Cost (£)">
            <input className="input" type="number" inputMode="decimal" step="0.01" min="0" value={v.cost ?? ''} onChange={(e) => set({ cost: e.target.value ? Number(e.target.value) : undefined })} />
          </Field>
          <Field label="Insurance">
            <Segmented
              value={v.claim ? 'claim' : 'no'}
              onChange={(x) => set({ claim: x === 'claim' })}
              options={[
                { value: 'no', label: 'Not claiming' },
                { value: 'claim', label: 'Claim it' },
              ]}
            />
          </Field>
        </div>
      </div>
    </Sheet>
  );
}
